import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaInfoCircle } from "react-icons/fa";
import HttpService from "../../../services/HttpService";
import UserService from "../../../services/UserService";

const getAssignedDebtors = () => {
	const assignedDebtors = UserService.getDebiNr();
	if (Array.isArray(assignedDebtors)) return assignedDebtors.map(String);
	if (assignedDebtors === undefined || assignedDebtors === null || assignedDebtors === "") return [];
	return String(assignedDebtors).split(",").map((debiNr) => debiNr.trim()).filter(Boolean);
};

const AccountCreation = ({ onCreated, onCancel }) => {
	const { t } = useTranslation();
	const [form, setForm] = useState({ email: "", firstName: "", lastName: "" });
	const [selectedDebtors, setSelectedDebtors] = useState([]);
	const [invoiceDebtor, setInvoiceDebtor] = useState("");
	const [selectedRoles, setSelectedRoles] = useState([]);
	const [roles, setRoles] = useState([]);
	const [companies, setCompanies] = useState([]);
	const [loading, setLoading] = useState(true);
	const [submitError, setSubmitError] = useState("");
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		let mounted = true;
		const http = HttpService.getAxiosClient();
		HttpService.configure();
		const assignedDebtors = getAssignedDebtors();

		Promise.all([
			http.get("/keycloak/roles"),
			assignedDebtors.length > 0
				? http.get(`/Klanten?limited=true&${assignedDebtors.map((debiNr) => `debiNr=${encodeURIComponent(debiNr)}`).join("&")}`)
				: Promise.resolve({ data: [] }),
		]).then(([rolesResponse, companiesResponse]) => {
			if (!mounted) return;
			const ownRoles = new Set(UserService.getRoles());
			setRoles((rolesResponse.data || []).filter((role) => ownRoles.has(role.name)));
			const loadedCompanies = (companiesResponse.data || []).map((name, index) => ({
				name,
				debiNr: assignedDebtors[index],
			}));
			setCompanies(loadedCompanies);
			if (assignedDebtors.length === 1) setInvoiceDebtor(assignedDebtors[0]);
		}).catch(() => {
			if (mounted) setSubmitError(t("accounts.creationLoadError"));
		}).finally(() => {
			if (mounted) setLoading(false);
		});

		return () => {
			mounted = false;
		};
	}, [t]);

	const handleChange = (event) => {
		setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
	};

	const toggleValue = (value, setter) => {
		setter((current) => current.includes(value)
			? current.filter((item) => item !== value)
			: [...current, value]);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setSubmitError("");
		if (selectedDebtors.length === 0 || selectedRoles.length === 0 || !invoiceDebtor) {
			setSubmitError(t("accounts.creationRequiredSelection"));
			return;
		}

		setSubmitting(true);
		try {
			const response = await HttpService.getAxiosClient().post("/Accounts", {
				...form,
				factuurdebiteur: invoiceDebtor,
				attributes: { debinr: selectedDebtors },
				roles: selectedRoles,
			});
			onCreated(response.data);
		} catch (err) {
			setSubmitError(t("accounts.creationError"));
		} finally {
			setSubmitting(false);
		}
	};

	if (loading) {
		return <div className="loading-container"><div className="loading-text">{t("accounts.creationLoading")}</div></div>;
	}

	return (
		<form className="account-creation-form" onSubmit={handleSubmit}>
			<div className="account-form-grid">
				<div className="filter-group">
					<label className="filter-label" htmlFor="account-email">{t("accounts.email")}</label>
					<input id="account-email" className="filter-input" name="email" type="email" required value={form.email} onChange={handleChange} />
				</div>
				<div className="filter-group">
					<label className="filter-label" htmlFor="account-first-name">{t("accounts.firstName")}</label>
					<input id="account-first-name" className="filter-input" name="firstName" required value={form.firstName} onChange={handleChange} />
				</div>
				<div className="filter-group">
					<label className="filter-label" htmlFor="account-last-name">{t("accounts.lastName")}</label>
					<input id="account-last-name" className="filter-input" name="lastName" required value={form.lastName} onChange={handleChange} />
				</div>
			</div>

			<fieldset className="account-selection-group">
				<legend className="filter-label account-field-label">
					{t("accounts.companies")}
					<span className="account-field-help">
						<button className="account-field-help-button" type="button" aria-label={t("accounts.companiesInfoLabel")}>
							<FaInfoCircle aria-hidden="true" />
						</button>
						<span className="account-field-help-tooltip" role="tooltip">{t("accounts.companiesInfo")}</span>
					</span>
				</legend>
				{companies.length > 0 ? companies.map((company) => (
					<label className="account-selection-option" key={company.debiNr}>
						<input type="checkbox" checked={selectedDebtors.includes(company.debiNr)} onChange={() => toggleValue(company.debiNr, setSelectedDebtors)} />
						{company.name}
					</label>
				)) : <p>{t("accounts.noCompanies")}</p>}
			</fieldset>

			<div className="filter-group account-invoice-debtor">
				<div className="filter-label account-field-label">
					<label htmlFor="account-invoice-debtor">{t("accounts.invoiceDebtor")}</label>
					<span className="account-field-help">
						<button className="account-field-help-button" type="button" aria-label={t("accounts.invoiceDebtorInfoLabel")}>
							<FaInfoCircle aria-hidden="true" />
						</button>
						<span className="account-field-help-tooltip" role="tooltip">{t("accounts.invoiceDebtorInfo")}</span>
					</span>
				</div>
				<select
					id="account-invoice-debtor"
					className="filter-select"
					value={invoiceDebtor}
					onChange={(event) => setInvoiceDebtor(event.target.value)}
					required
				>
					<option value="">{t("accounts.selectInvoiceDebtor")}</option>
					{companies.map((company) => (
						<option key={company.debiNr} value={company.debiNr}>{company.name}</option>
					))}
				</select>
			</div>

			<fieldset className="account-selection-group">
				<legend className="filter-label">{t("accounts.availableRoles")}</legend>
				{roles.length > 0 ? roles.map((role) => (
					<label className="account-selection-option" key={role.name} title={role.description}>
						<input type="checkbox" checked={selectedRoles.includes(role.name)} onChange={() => toggleValue(role.name, setSelectedRoles)} />
						<span><strong>{role.name}</strong><small>{role.description}</small></span>
					</label>
				)) : <p>{t("accounts.noRoles")}</p>}
			</fieldset>

			{submitError && <div className="error-alert"><p>{submitError}</p></div>}
			<div className="action-buttons">
				<button className="btn btn-primary" type="submit" disabled={submitting}>{submitting ? t("accounts.creating") : t("accounts.create")}</button>
				<button className="btn btn-secondary" type="button" onClick={onCancel}>{t("common.cancel")}</button>
			</div>
		</form>
	);
};

export default AccountCreation;
